/**
 * useAgents Hook
 * React Query hook para gestión de agentes con server state
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { agentsApi } from '@/lib/api/agents';
import { useAgentsStore } from '@/store/agentsStore';
import type { AgentPolicy, AgentCommand } from '@/types/agent';

const QUERY_KEYS = {
  agents: ['agents'] as const,
  agent: (id: string) => ['agents', id] as const,
  metrics: (id: string) => ['agents', id, 'metrics'] as const,
  policy: (id: string) => ['agents', id, 'policy'] as const,
};

/**
 * Hook para cargar todos los agentes
 */
export const useAgents = () => {
  const { setAgents } = useAgentsStore();

  return useQuery({
    queryKey: QUERY_KEYS.agents,
    queryFn: async () => {
      const snapshot = await agentsApi.getAgentsSnapshot();
      setAgents(snapshot.agents, snapshot.lifecycleRevision, true);
      return snapshot.agents;
    },
    refetchInterval: 30000, // Refetch every 30s as fallback
    staleTime: 10000, // Consider data stale after 10s
  });
};

/**
 * Hook para cargar un agente específico
 */
export const useAgent = (agentId: string | null) => {
  return useQuery({
    queryKey: QUERY_KEYS.agent(agentId!),
    queryFn: () => agentsApi.getAgent(agentId!),
    enabled: !!agentId,
    retry: false,
  });
};

/**
 * Hook para cargar métricas de un agente
 */
export const useAgentMetrics = (agentId: string | null) => {
  const { updateAgentMetrics } = useAgentsStore();

  return useQuery({
    queryKey: QUERY_KEYS.metrics(agentId!),
    queryFn: async () => {
      const metrics = await agentsApi.getAgentMetrics(agentId!);
      if (agentId) {
        updateAgentMetrics(agentId, metrics);
      }
      return metrics;
    },
    enabled: !!agentId,
    refetchInterval: 10000, // Poll cada 10s (WS empuja en vivo; esto es fallback)
    refetchIntervalInBackground: false, // No pollear con la pestaña oculta
    staleTime: 5000,
  });
};

/**
 * Hook para cargar policy de un agente
 */
export const useAgentPolicy = (agentId: string | null) => {
  return useQuery({
    queryKey: QUERY_KEYS.policy(agentId!),
    queryFn: () => agentsApi.getAgentPolicy(agentId!),
    enabled: !!agentId,
  });
};

/**
 * Mutation para eliminar agente
 */
export const useDeleteAgent = () => {
  const queryClient = useQueryClient();
  const { removeAgent, selectAgent } = useAgentsStore();

  return useMutation({
    mutationFn: agentsApi.deleteAgent,
    onSuccess: async (lifecycleRevision, agentId) => {
      removeAgent(agentId, lifecycleRevision);
      selectAgent(null);
      // Remove every cached representation immediately. A refetch remains a
      // fallback, while the store guards against an old WS snapshot.
      queryClient.removeQueries({ queryKey: QUERY_KEYS.agent(agentId) });
      queryClient.removeQueries({ queryKey: QUERY_KEYS.metrics(agentId) });
      queryClient.removeQueries({ queryKey: QUERY_KEYS.policy(agentId) });
      queryClient.setQueryData<unknown[]>(QUERY_KEYS.agents, (current) =>
        current?.filter((item) =>
          typeof item === 'object' && item !== null && 'agentId' in item
            ? (item as { agentId: string }).agentId !== agentId
            : true
        )
      );
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agents });
    },
  });
};

/**
 * Mutation para actualizar policy
 */
export const useUpdateAgentPolicy = (agentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (policy: AgentPolicy) => agentsApi.updateAgentPolicy(agentId, policy),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.policy(agentId) });
    },
  });
};

/**
 * Mutation para actualizar hostname
 */
export const useUpdateAgentHostname = (agentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (hostname: string) => agentsApi.updateAgentHostname(agentId, hostname),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agent(agentId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agents });
    },
  });
};

/**
 * Mutation para enviar comando
 */
export const useSendCommand = (agentId: string) => {
  return useMutation({
    mutationFn: (command: AgentCommand) => agentsApi.sendCommand(agentId, command),
  });
};
